import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';
import { CHAT_UI_MAX_OFFERS } from '../../shared/schema/limits.js';
import { ChatUiMoneySchema } from '../../shared/schema/money_schema.js';
import { TravelerRefSchema } from '../../shared/schema/traveler_ref.js';
import { HotelAmenitySchema } from '../shared/hotel_amenity_schema.js';
import { HotelPhotoSchema } from '../shared/hotel_photo_schema.js';
import { nightsBetween } from '../shared/nights.js';

/*
 * **Os schemas de hotel deste wire NÃO são `.strict()`, e isso é decisão.**
 *
 * `.strict()` faz o `safeParse` REJEITAR o objeto inteiro quando aparece uma
 * chave que ele não conhece. Num contrato que a AGENTS.md manda evoluir de forma
 * aditiva, quem paga essa conta é o bundle já baixado: o navegador do cliente
 * carrega o schema da versão dele, o servidor passa a emitir um campo novo, e o
 * `hotel_planner` inteiro some da conversa em silêncio — sem erro na tela, sem
 * ordem de deploy que conserte, porque o código velho está na máquina dela.
 *
 * Sem `strict` o Zod DESCARTA a chave desconhecida e devolve o resto: o card
 * antigo ignora o campo que não sabe ler e continua desenhando. É o
 * comportamento que "evolução aditiva" já prometia.
 *
 * O que se perde é o flagra de erro de digitação do produtor no parse. Ele não
 * fica sem rede: os builders são testados pelo payload que emitem, e é lá que a
 * chave errada aparece — no produtor, que é onde dá pra consertar.
 *
 * Não há risco de perder dado no descarte: o bloco não faz round-trip. As
 * actions sobem id e decisão (`GenUiAction`), nunca o payload de volta.
 *
 * Os schemas de voo e de carro seguem `.strict()` e carregam a mesma armadilha.
 */
const HotelTravelersSchema = z.array(TravelerRefSchema).min(1).max(9);

/** Ocupação do quarto em HÓSPEDES, sem distinguir idade: o fornecedor precisa
 *  saber quantas camas, e criança some quando o campo só conta adulto.
 *
 *  O campo se chamou `adults` até 2026-08-07 e blocos persistidos ainda carregam
 *  esse nome — o `preprocess` é a migração de leitura que o replay exige. Ela
 *  reescreve a chave antiga SOMENTE quando a nova está ausente, então num
 *  payload misto o nome NOVO sempre ganha e o antigo é descartado — a escolha é
 *  determinística, não uma corrida entre as duas. */
/** Teto de hóspedes por QUARTO — a mesma régua do wire (`HotelRoomOccupancySchema`)
 *  e do campo de ocupação; exportado para quem pré-preenche ocupação (o replay
 *  de um recente) respeitar o teto em vez de fabricar um quarto inválido. */
export const HOTEL_MAX_GUESTS_PER_ROOM = 3;

export const HotelRoomOccupancySchema = z.preprocess(
  (value) =>
    typeof value === 'object' && value !== null && 'adults' in value && !('guests' in value)
      ? { guests: value.adults }
      : value,
  z.object({ guests: z.number().int().min(1).max(HOTEL_MAX_GUESTS_PER_ROOM) }),
);
export type HotelRoomOccupancy = z.infer<typeof HotelRoomOccupancySchema>;

const HotelRoomsSchema = z.array(HotelRoomOccupancySchema).min(1).max(5);

export const HotelRoomSchema = z.object({
  description: z.string().optional(),
  boardDescription: z.string().optional(),
  refundable: z.boolean().optional(),
  breakfast: z.boolean().optional(),
  total: ChatUiMoneySchema.optional(),
  supplier: z
    .object({
      name: z.string().optional(),
      image: z.string().optional(),
      icon: z.string().optional(),
    })

    .optional(),
  freeCancellationUntil: z.iso.date().optional(),
  /** A política de cancelamento INTEIRA, como o fornecedor a estrutura: cada
   *  janela diz até quando vale e qual a multa depois dela. O
   *  `freeCancellationUntil` acima é o resumo (só a data mais cedo) e continua
   *  valendo para o card de quarto; a seção "Política de cancelamento" do
   *  detalhe precisa das MULTAS, que o resumo joga fora. Evolução ADITIVA:
   *  bloco antigo sem o campo continua parseando. */
  cancellationPolicies: z
    .array(
      z.object({
        from: z.iso.date().optional(),
        until: z.iso.date().optional(),
        penalty: ChatUiMoneySchema.optional(),
      }),
    )
    .max(6)
    .optional(),
  /* O recorte carrega o que o card de quarto do drawer mostra (a projeção
   * `buildHotelRoomsBlock` só repassa) — os campos são os do resume que o
   * agents já guarda (`HotelRoomSummary`). Evolução ADITIVA: bloco antigo
   * sem eles continua parseando, e cada peça da pele é fail-soft. */
  name: z.string().optional(),
  /** Prosa do quarto vinda do detalhe do fornecedor (área, cama, ocupação em
   *  texto corrido). Separada de `description`, que aqui é o NOME da
   *  acomodação na tarifa. */
  about: z.string().optional(),
  photos: z.array(HotelPhotoSchema).max(8).optional(),
  amenities: z.array(HotelAmenitySchema).max(24).optional(),
  policyStatus: z.enum(['within_policy', 'out_of_policy', 'unknown']).optional(),
  /** As REGRAS de política que esta tarifa quebrou (chaves do `HotelPolicies`
   *  do fornecedor, ordem canônica do projetor). O `policyStatus` diz
   *  dentro/fora; isto diz POR QUÊ, para o agente responder pela regra em vez
   *  de deduzir. Evolução ADITIVA: bloco antigo sem o campo segue parseando. */
  policyViolations: z.array(z.string()).max(12).optional(),
  negotiated: z.boolean().optional(),
  quantity: z.number().int().min(0).optional(),
  totalPerNight: ChatUiMoneySchema.optional(),
});
export type ChatUiHotelRoom = z.infer<typeof HotelRoomSchema>;

/** Os LIMITES da política do viajante nesta busca — o outro lado do motivo.
 *  Os preços vêm SEM moeda (a origem não a informa): quem renderiza diz "na
 *  moeda da política", nunca R$. Booleanos só chegam quando a regra está
 *  LIGADA. Tudo opcional e aditivo. Nomeado porque viaja por DOIS caminhos: na
 *  oferta (`HotelOfferSchema.policyLimits`) e, uma só vez, no contexto de
 *  refino (`HotelPlannerRefineContextSchema.policyLimits`). */
export const HotelPolicyLimitsSchema = z.object({
  policyName: z.string().optional(),
  maxNightly: z.number().optional(),
  maxNightlyInternational: z.number().optional(),
  maxNightlyCity: z.number().optional(),
  maxNightlyCountry: z.number().optional(),
  minRating: z.number().optional(),
  onlyBreakfast: z.boolean().optional(),
  onlyRefundable: z.boolean().optional(),
  bookingWindowDays: z.number().optional(),
  bookingWindowInternationalDays: z.number().optional(),
  cheapestRateOnly: z.boolean().optional(),
  cheapestHotelOnly: z.boolean().optional(),
});

export const HotelOfferSchema = z.object({
  offerId: z.string().min(1),
  title: z.string().min(1),
  summary: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  address: z.string().optional(),
  category: z.number().min(0).max(5).optional(),
  // Nota 0–5 (escala Google, a mesma de `category`); 0 significa "sem
  // avaliação". O teto 10 fica só por compatibilidade de replay de blocos
  // antigos — nada produz >5 hoje. Ver `intelligence-ui-kit/src/hotel/rating_band.ts`.
  rating: z.number().min(0).max(10).optional(),
  /** Quantas avaliações sustentam a `rating` — `userRatingCount` do fornecedor.
   *  Nota sem lastro engana: 5,0 de 3 avaliações e 4,6 de 3.218 são a mesma
   *  string e confianças opostas. É por ele que a lista ordena por "Avaliações". */
  reviewCount: z.number().int().nonnegative().optional(),
  price: ChatUiMoneySchema.optional(),
  recommended: z.boolean().optional(),
  refundable: z.boolean().optional(),
  breakfast: z.boolean().optional(),
  policyStatus: z.enum(['within_policy', 'out_of_policy', 'unknown']).optional(),
  /** Igual ao do quarto, no nível da OFERTA (tarifa mais barata do hotel). */
  policyViolations: z.array(z.string()).max(12).optional(),
  /** Os LIMITES da política do viajante nesta busca — ver
   *  `HotelPolicyLimitsSchema`. */
  policyLimits: HotelPolicyLimitsSchema.optional(),
  thumbnailUrl: z.string().optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  /** Distância até o ponto pesquisado, em km, como o FORNECEDOR mediu
   *  (`localization.distance`). Existe porque a conta do cliente depende de o
   *  bloco carregar a âncora da busca: sem `placeDetails`, o haversine não tem
   *  de onde medir e o card fica sem a linha que o eixo ③ decide (R-hotel-1).
   *  Quando os dois existem, batem — conferido contra develop em 2026-08-10. */
  distanceKm: z.number().nonnegative().optional(),
  photos: z.array(HotelPhotoSchema).max(12).optional(),
  amenities: z.array(HotelAmenitySchema).max(24).optional(),
  /** Sem teto: na busca isto carrega só o quarto mais barato, e na leitura
   *  hidratada (`/hotel/offer`) carrega a lista completa que o cartão compara. */
  rooms: z.array(HotelRoomSchema).optional(),
  /** Marca "este é o hotel que você pesquisou" no resultado da busca — o
   *  modal-session emite em `modalData.selectedHotel` (`hotel_search_mapper.ts`).
   *  Ausente/`undefined` em bloco antigo (replay), nunca `false` explícito. */
  selectedHotel: z.boolean().optional(),
  /** Histórico pessoal: o viajante JÁ se hospedou neste hotel. O modal-session
   *  carimba `modalData.stayedBefore`/`stayCount` na oferta persistida
   *  (`hotel_stay_history.ts`), e SÓ as rotas determinísticas do BFF repassam —
   *  os projetores do card conversacional seguem calados (ver o gate documentado
   *  em `hotel_offer_projection.ts`, o mesmo de `selectedHotel`). Presente ⇒ o
   *  card ganha o selo "Você ficou aqui" no lugar do eixo (`offerBadges`). */
  stayedBefore: z.boolean().optional(),
  /** Quantas estadias sustentam o `stayedBefore` — "· N×" no selo quando ≥ 2. */
  stayCount: z.number().int().positive().optional(),
  /** Favorito DECLARADO: o viajante da busca corrente marcou este hotel com o
   *  coração (item 1 F2). O modal-session carimba `modalData.favorite` na
   *  oferta persistida (`hotel_search_ranking.ts`), e — como `stayedBefore` —
   *  SÓ as rotas determinísticas do BFF repassam; os projetores do card
   *  conversacional seguem calados (o mesmo gate documentado em
   *  `hotel_offer_projection.ts`). Presente ⇒ selo "Favorito" no card, ACIMA
   *  de "Você ficou aqui" (colisão = card único com o selo Favorito; o
   *  `stayedBefore` continua no dado), e o estado inicial do coração. */
  favorite: z.boolean().optional(),
  /** Hotel com ACORDO da empresa do viajante (#6359): o fornecedor marca
   *  `agreedRate` na busca e o modal-session grava em `modalData.agreedRate`.
   *  Como `favorite`, SÓ as rotas determinísticas do BFF repassam (o mesmo
   *  gate documentado em `hotel_offer_projection.ts`). `true` ⇒ selo "Hotel com
   *  acordo" no card e na Página do Hotel; ausente ⇒ nada a dizer. */
  agreedRate: z.boolean().optional(),
  /** Agregado no nível do HOTEL: existe ALGUMA tarifa com café da manhã (itens
   *  6/7 da planilha). Diferente de `breakfast` acima — aquele é do quarto mais
   *  barato exibido; este habilita o FILTRO "Café da manhã" da lista, que quer
   *  "o hotel oferece café", não "a tarifa mais barata inclui". O modal-session
   *  deriva de `resume.cheapestRoomBreakFast` e SÓ as rotas determinísticas do
   *  BFF repassam (mesmo gate de `stayedBefore`/`favorite`). Ausente ⇒
   *  desconhecido (o filtro não exclui); `false` ⇒ o fornecedor afirma que não há. */
  hasBreakfastRate: z.boolean().optional(),
  /** Agregado no nível do HOTEL: existe ALGUMA tarifa reembolsável/cancelável
   *  (item 7). Espelho de `hasBreakfastRate` para o filtro "Reembolsável";
   *  derivado de `resume.cheapestRoomRefundable`, mesmo gate e mesma semântica
   *  de ausência (desconhecido ≠ `false`). */
  hasRefundableRate: z.boolean().optional(),
  /** Existe UMA tarifa com café E reembolso (#5849): `hasBreakfastRate` e
   *  `hasRefundableRate` podem vir de tarifas diferentes. Mesmo gate e mesma
   *  semântica de ausência. */
  hasBreakfastRefundableRate: z.boolean().optional(),
  /** Se a tarifa mais barata COM café / reembolsável / café+reembolsável está
   *  dentro da política (#5820). O filtro "Dentro da política" combinado com
   *  café/reembolso lê ESTE fato — uma tarifa só que atenda aos dois —, e não o
   *  `policyStatus` do quarto mais barato. Mesmo gate de `hasBreakfastRate`;
   *  ausente ⇒ desconhecido (vale o `policyStatus`, como antes). */
  breakfastRateWithinPolicy: z.boolean().optional(),
  refundableRateWithinPolicy: z.boolean().optional(),
  breakfastRefundableRateWithinPolicy: z.boolean().optional(),
  /** O PREÇO da tarifa reembolsável mais barata do hotel — o par de
   *  `hasRefundableRate` (#3404). O `price` do card é do quarto mais barato, e
   *  a tarifa reembolsável pode ser outro quarto, mais caro: sem este número o
   *  card dizia "reembolsável" ao lado de um preço que não é dela. Mesma régua
   *  do `price` (total da estadia; o card divide pelas noites). Só viaja quando
   *  `hasRefundableRate` é `true`, pelo mesmo gate determinístico. */
  refundableRateFrom: ChatUiMoneySchema.optional(),
});
export type ChatUiHotelOffer = z.infer<typeof HotelOfferSchema>;

export const HotelPlannerPlaceSchema = z.object({
  placeId: z.string().min(1),
  // Faixa geográfica: o lugar sintético da área do mapa (#3114) chega sem
  // passar pelo Places, então a fronteira é a única guarda das coordenadas.
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  name: z.string().min(1).optional(),
  city: z.string().min(1).optional(),
  cityId: z.string().min(1).optional(),
  state: z.string().min(1).optional(),
  country: z.string().min(1).optional(),
  types: z.array(z.string().min(1)).optional(),
  fullAddress: z.array(z.record(z.string(), z.unknown())).optional(),
  maxDistance: z.number().positive().optional(),
});
export type HotelPlannerPlace = z.infer<typeof HotelPlannerPlaceSchema>;

export const HotelSearchPreviewResultSchema = z.object({
  offers: z.array(HotelOfferSchema).max(CHAT_UI_MAX_OFFERS),
  placeDetails: HotelPlannerPlaceSchema.optional(),
  nights: z.number().int().positive(),
  /** A sessão em que esta busca rodou. Volta para o próximo refino cair na
   *  MESMA sessão — inclusive quando o host teve de mintar uma (bloco antigo
   *  sem sessão, ou a do bloco não existe mais). */
  sessionId: z.string().min(1).optional(),
});
export type HotelSearchPreviewResult = z.infer<typeof HotelSearchPreviewResultSchema>;

function hasDestination(value: {
  place?: string;
  placeRef?: string;
  placeDetails?: HotelPlannerPlace;
  destinationId?: number;
}): boolean {
  return Boolean(
    value.destinationId || value.placeDetails || value.placeRef || value.place?.trim(),
  );
}

function hasValidStay(value: { checkin?: string; checkout?: string }): boolean {
  return Boolean(value.checkin && value.checkout && value.checkout > value.checkin);
}

/** Teto de noites de UMA busca de hotel — o do fornecedor: acima disso a
 *  voll-business recusa a `searchHotel` ("período de 1 até 30 dias", #5315).
 *  O card trava o Buscar e o BFF responde 400 em vez de levar a recusa ao
 *  fornecedor. */
export const HOTEL_MAX_STAY_NIGHTS = 30;

export function exceedsHotelMaxStay(value: { checkin: string; checkout: string }): boolean {
  return nightsBetween(value.checkin, value.checkout) > HOTEL_MAX_STAY_NIGHTS;
}

/** De onde veio o destino escolhido, quando isso muda o que o BFF faz com ele:
 *  `geolocation` é o lugar REAL que a posição do navegador virou ("Minha
 *  localização") — um pick como outro qualquer, exceto que o BFF não o grava
 *  como recente: o endereço atual da pessoa não é um lugar que ela
 *  reconheceria numa lista de atalhos amanhã. `map_area` é a ÁREA VISÍVEL do
 *  mapa que a pessoa arrastou ("Buscar nesta área", #3114): um lugar
 *  SINTÉTICO — `placeDetails` com `MAP_AREA_PLACE_ID`, o centro da área como
 *  coordenada e nada de cidade —, que o BFF não re-hidrata pelo id, não grava
 *  como recente e busca no raio pedido (`searchRadiusKm`), não no padrão.
 *  Ausente = escolhido/digitado. Evolução ADITIVA: bloco antigo sem o campo
 *  segue válido. */
export const HotelPlaceSourceSchema = z.enum(['geolocation', 'map_area']);
export type HotelPlaceSource = z.infer<typeof HotelPlaceSourceSchema>;

/** O `placeId` do lugar sintético da busca na área do mapa — marcador
 *  compartilhado por cliente e BFF, como o `my_location` legado foi. */
export const MAP_AREA_PLACE_ID = 'map_area';

/** Teto do raio que a área do mapa pode pedir ao fornecedor (km). Acima
 *  disso o viewport é um estado inteiro, e o fornecedor devolveria o país.
 *  O host aplica a guarda ao montar o pedido (o mapa mede sem teto); o schema
 *  a repete na fronteira, para o BFF nunca receber um raio que rejeitaria. */
export const HOTEL_MAP_AREA_MAX_RADIUS_KM = 200;

export const HotelPlannerSelectionSchema = z
  .object({
    action: z.literal('search'),
    place: z.string().optional(),
    /** Ref do item que a pessoa ESCOLHEU na busca (`HotelPlaceOption.ref`).
     *  Sem `placeDetails` (linha de busca não tem coordenada), é ele que o BFF
     *  hidrata via `resolveLocation` — o degrau que mata o palpite por texto. */
    placeRef: z.string().min(1).optional(),
    placeDetails: HotelPlannerPlaceSchema.optional(),
    placeSource: HotelPlaceSourceSchema.optional(),
    /** O raio da busca em km, só com `placeSource: 'map_area'`: a metade da
     *  diagonal da área visível. Fora dela o BFF ignora e busca no padrão. */
    searchRadiusKm: z.number().positive().max(HOTEL_MAP_AREA_MAX_RADIUS_KM).optional(),
    destinationId: z.number().int().positive().optional(),
    checkin: z.iso.date(),
    checkout: z.iso.date(),
    rooms: HotelRoomsSchema.optional(),
    maxPrice: z.number().nonnegative().optional(),
    onlyBreakfast: z.boolean().optional(),
    onlyRefundable: z.boolean().optional(),
    onlyPolicyWithIn: z.boolean().optional(),
    travelers: HotelTravelersSchema.optional(),
    policyTravelerId: z.string().min(1).optional(),
  })

  .superRefine((value, ctx) => {
    if (!hasDestination(value)) {
      ctx.addIssue({
        code: 'custom',
        path: ['place'],
        message: 'Hotel planner search requires a place or destinationId',
      });
    }
    // A área do mapa é SEMPRE o lugar sintético: com um `placeId` real aqui o
    // BFF re-hidrataria o lugar pelo id e trocaria o centro da área pelo dele.
    if (value.placeSource === 'map_area' && value.placeDetails?.placeId !== MAP_AREA_PLACE_ID) {
      ctx.addIssue({
        code: 'custom',
        path: ['placeDetails', 'placeId'],
        message: 'Map-area searches require the synthetic map-area place',
      });
    }
    if (!hasValidStay(value)) {
      ctx.addIssue({
        code: 'custom',
        path: ['checkout'],
        message: 'Hotel planner checkout must be after checkin',
      });
    } else if (exceedsHotelMaxStay(value)) {
      ctx.addIssue({
        code: 'custom',
        path: ['checkout'],
        message: `Hotel planner stay must not exceed ${String(HOTEL_MAX_STAY_NIGHTS)} nights`,
      });
    }
  });
export type HotelPlannerSelection = z.infer<typeof HotelPlannerSelectionSchema>;

/**
 * Contexto de REFINO do card de hotel (#2477). A busca feita DENTRO do card é
 * client-side (`POST /hotel/search-preview`, sem turno), então o FSM do agente
 * nunca aprende os critérios/sessão que o viajante viu na tela. Quando ele digita
 * um refino no chat ("filtrar por café da manhã") com um `hotel_planner` VIVO em
 * cena, o host anexa este contexto em `confirmation.selections`: o agente hidrata
 * os slots (lugar + datas + ocupação) e a sessão ANTES do mapper, e o refino
 * re-busca a partir do que já estava na tela — em vez de responder
 * `missing_criteria` e reemitir um planner VAZIO por cima da lista de ofertas.
 *
 * `kind` é o DISCRIMINANTE que separa este contexto do `submit_planner_search`
 * (a busca do card SUPERADO, que envia a `HotelPlannerSelection` CRUA): o
 * submit-guard do hotel tenta a seleção crua primeiro (→ busca no agente) e só
 * então este contexto (→ hidratar, SEM decisão — o texto livre segue para o
 * LLM). `HotelPlannerSelectionSchema` exige `action: 'search'` + `checkin` +
 * `checkout` no TOPO, ausentes aqui, então as duas formas nunca se confundem.
 */
/** Teto de comodidades por oferta no contexto de refino — o MESMO que a cena do
 *  agente aplica (`MAX_LIST_AMENITIES` em `core/state.ts`): mandar mais é peso
 *  morto no wire, o agente descartaria. */
export const HOTEL_REFINE_AMENITIES_CAP = 6;

/**
 * A oferta como viaja no contexto de REFINO (#2628): só o que a cena do agente
 * lê (`toSceneOffer`, em `core/state.ts`). A forma completa (`HotelOfferSchema`:
 * até 12 fotos, 24 amenities, quartos, descrição) é a do CARD, e um refino de
 * texto com 6 delas em `confirmation.selections` passava dos 8 KB do
 * `SizeRestrictions_BODY` do WAF na borda de prod — o `POST /messages` voltava
 * 403 antes de chegar na aplicação ("falha ao enviar").
 *
 * `pick` de `HotelOfferSchema`: um payload GORDO antigo (bundle do web-chat em
 * cache) continua parseando — o `z.object` descarta as chaves desconhecidas, e
 * as amenities legadas (até 24) são CORTADAS no teto antes da validação, nunca
 * recusadas. `toHotelRefineOffer` (`hotel_refine_offer.ts`) é o projetor que
 * produz isto.
 */
export const HotelRefineOfferSchema = HotelOfferSchema.pick({
  offerId: true,
  title: true,
  city: true,
  country: true,
  category: true,
  rating: true,
  reviewCount: true,
  price: true,
  refundable: true,
  breakfast: true,
  policyStatus: true,
  /** O MOTIVO viaja junto do veredito: sem ele, um refino por TEXTO ("só os com
   *  café") reconstrói a cena a partir deste recorte
   *  (`hydrateHotelFromPlannerRefine` → `toSceneOffer`) e o agente perderia em
   *  silêncio a razão de o hotel estar fora da política. São poucas chaves
   *  curtas por oferta, e o orçamento do WAF já cede ofertas do FIM quando
   *  aperta (`hotel_refine_budget.ts`). O `policyLimits` NÃO entra: ele se
   *  repete idêntico em toda oferta e pagaria o orçamento N vezes. */
  policyViolations: true,
  latitude: true,
  longitude: true,
  distanceKm: true,
}).extend({
  amenities: z
    .preprocess(
      (value) => (Array.isArray(value) ? value.slice(0, HOTEL_REFINE_AMENITIES_CAP) : value),
      z.array(HotelAmenitySchema).max(HOTEL_REFINE_AMENITIES_CAP),
    )
    .optional(),
});
export type HotelRefineOffer = z.infer<typeof HotelRefineOfferSchema>;

/** M1 · 1.1b — o PAINEL "Ver todos os hotéis" reporta estado ao agente. A
 *  união de ordenação é a REAL do painel (`HotelResultsOrder`, ui-kit), e os
 *  filtros espelham `HotelResultsFilterValue` campo a campo, com nomes de
 *  wire curtos. Sem `mapCenter` (PII quando deriva de "Minha localização") e
 *  sem categoria (o painel não tem esse eixo). Tudo opcional e aditivo. */
export const HOTEL_PANEL_SORT_KEYS = [
  'recommended',
  'price',
  'distance',
  'reviews',
  'stars',
] as const;
export const HotelPanelSortSchema = z.enum(HOTEL_PANEL_SORT_KEYS);
export type HotelPanelSort = z.infer<typeof HotelPanelSortSchema>;

/** Teto de comodidades no filtro reportado — o `panel` inteiro tem 400 bytes
 *  de orçamento medido no web-chat (ver `hotel_refine_budget.ts`). */
export const HOTEL_PANEL_AMENITIES_CAP = 5;
/** Teto de ÍNDICES visíveis — 30 inteiros cabem em ~100 bytes; 30 ids opacos
 *  (~45 chars cada) não caberiam em orçamento nenhum. */
export const HOTEL_PANEL_VISIBLE_CAP = 30;
/** Teto dos NOMES das linhas visíveis fora de `offers` (`visibleBeyond`,
 *  #3325) e do tamanho de cada nome: 12 × 60 chars são ~800 bytes no pior
 *  caso (típico: 2–8 nomes curtos) — cabe atrás das ofertas no orçamento do
 *  web-chat, que cede o `panel` inteiro antes de cortar o envio. Só nomes,
 *  sem id: abrir por nome já resolve pelo título. */
export const HOTEL_PANEL_BEYOND_CAP = 12;
export const HOTEL_PANEL_BEYOND_TITLE_CAP = 60;

/** Pisos das FAIXAS de nota do painel, do topo pro chão — espelho local de
 *  `HOTEL_RATING_BAND_FLOORS` (`intelligence-ui-kit`), que vive num módulo
 *  React e não pode ser importado daqui: este pacote é o contrato de wire,
 *  zero-React para os consumidores de backend (o
 *  agents projeta a cena por `hotel_planner_directives.ts`). A cópia é travada
 *  por teste (`hotel_planner_directives.test.ts`) e pelo tipo de
 *  `panelRatingBands` (`use_hotel_panel_list.ts`), que só compila se as duas
 *  listas coincidirem.
 *
 *  É TAMBÉM a régua em que o AGENTE ENCOSTA o piso que o modelo pediu
 *  (`directiveRatingFloor`, ao mesclar a diretiva — agents `mergeFilter`).
 *  O modelo pede o piso cru ("acima de 4,8" → 4,8): fechar a ferramenta num
 *  `enum` destas faixas (prod 17/09, #3103) fez o modelo mandar `null` quando
 *  o piso não cabia (prod 21/09, #3393) — e a fala do filtro já nasce do
 *  resultado, encostada, não da prosa do modelo. */
export const HOTEL_DIRECTIVE_RATING_FLOORS = [4.5, 4, 3.5] as const;
export type HotelDirectiveRatingFloor = (typeof HOTEL_DIRECTIVE_RATING_FLOORS)[number];

export const HotelPanelFiltersSchema = z.object({
  policyOnly: z.boolean().optional(),
  breakfastOnly: z.boolean().optional(),
  refundableOnly: z.boolean().optional(),
  minNightlyPrice: z.number().nonnegative().optional(),
  maxNightlyPrice: z.number().nonnegative().optional(),
  /** Pisos de nota (0–10) — `HotelRatingBandFloor` quando é o PAINEL que
   *  relata; largo (0–10) pelo replay de blocos antigos. O que o AGENTE grava
   *  já vem encostado nas faixas (`HOTEL_DIRECTIVE_RATING_FLOORS`). */
  ratingFloors: z.array(z.number().min(0).max(10)).max(4).optional(),
  maxDistanceKm: z.number().nonnegative().optional(),
  amenityIds: z.array(z.string().min(1)).max(HOTEL_PANEL_AMENITIES_CAP).optional(),
});
export type HotelPanelFilters = z.infer<typeof HotelPanelFiltersSchema>;

export const HotelPanelStateSchema = z.object({
  /** A lista completa (ou o mapa cheio) está aberta AGORA. */
  open: z.boolean(),
  /** Ausente = `recommended` (a ordem padrão não é reportada). */
  sortBy: HotelPanelSortSchema.optional(),
  filters: HotelPanelFiltersSchema.optional(),
  /** Quantas linhas o painel mostra agora (inclui ofertas paginadas que o
   *  agente não conhece). Com o painel FECHADO só sai quando há filtro/ordem
   *  do viajante ainda aplicados (#2857): é o que o filtro deixou. */
  shown: z.number().int().nonnegative().optional(),
  /** ÍNDICES em `offers` das linhas visíveis, na ordem da tela — mesma regra
   *  de `shown` para o painel fechado. */
  visible: z.array(z.number().int().nonnegative()).max(HOTEL_PANEL_VISIBLE_CAP).optional(),
  /** NOMES das linhas visíveis que NÃO estão em `offers` (paginadas além do
   *  card), na ordem da tela — sem eles o agente contava "4 de 14" e só sabia
   *  nomear as da cena (#3325, prod 19/09). Mesma regra de `shown`; ausente
   *  quando nenhuma linha visível está fora de `offers`. */
  visibleBeyond: z
    .array(z.string().min(1).max(HOTEL_PANEL_BEYOND_TITLE_CAP))
    .max(HOTEL_PANEL_BEYOND_CAP)
    .optional(),
  /** Hotel com a gaveta de detalhes aberta pelo painel/dock. */
  openOfferId: z.string().min(1).optional(),
});
export type HotelPanelState = z.infer<typeof HotelPanelStateSchema>;

/** M1 · 1.2 — a via de VOLTA: o agente dirige o planner VIVO. Aditivo e
 *  não-estrito (um bundle antigo descarta a chave e desenha sem diretivas).
 *  `highlightOfferIds` são ids EM CENA (o reduce do agents os casa); filtro e
 *  ordem são os do painel (`HotelPanelFiltersSchema`/`HotelPanelSortSchema`),
 *  aplicados pela MESMA função pura (`applyHotelPlannerDirectives`) no card e
 *  na projeção de cena do agente — a invariante #2469 exige uma só lista. */
export const HotelPlannerDirectivesSchema = z.object({
  highlightOfferIds: z.array(z.string().min(1)).max(6).optional(),
  sortBy: HotelPanelSortSchema.optional(),
  filter: HotelPanelFiltersSchema.optional(),
  open: z.enum(['panel', 'map']).optional(),
});
export type HotelPlannerDirectives = z.infer<typeof HotelPlannerDirectivesSchema>;

export const HotelPlannerRefineContextSchema = z.object({
  kind: z.literal('hotel_planner_refine'),
  /** `ui.id` do planner que produziu a busca — é o id que uma reemissão do
   *  agente reusa (diretivas) para NÃO congelar o card em que o viajante
   *  trabalhava. Opcional: contexto antigo não o traz. */
  blockId: z.string().min(1).max(64).optional(),
  selection: HotelPlannerSelectionSchema,
  /** A sessão modal-session que produziu as ofertas em cena, quando o host a
   *  conhece — o refino re-busca NELA em vez de mintar outra, mantendo os
   *  offerIds da tela resolvíveis pelo agente. Ausente: o agente minta uma nova. */
  sessionId: z.string().min(1).optional(),
  /** As ofertas que o CARD está mostrando (busca client-side, #2477 gap): a
   *  busca dentro do `hotel_planner` nunca passa pelo FSM, então sem elas
   *  `state.offers` fica vazio e o agente nega ofertas que o cliente vê na
   *  tela ("qual hotel tem academia?" → "não temos ofertas em cena"). O agente
   *  as projeta na cena para a cena BATER com o card (#2469: cena = card).
   *  Ausente/vazio: comportamento anterior, sem hidratar ofertas. Forma ENXUTA
   *  (`HotelRefineOfferSchema`, #2628) — o corpo do `POST /messages` tem de
   *  caber nos 8 KB do WAF. */
  offers: z.array(HotelRefineOfferSchema).max(30).optional(),
  panel: HotelPanelStateSchema.optional(),
  /** Os limites são do VIAJANTE, idênticos em toda oferta da busca — por isso
   *  viajam UMA vez aqui, e não por oferta (`HotelRefineOfferSchema` deixa o
   *  `policyLimits` de fora de propósito: N cópias pagariam o orçamento do WAF
   *  N vezes). O agente hidrata `state.policyLimits` a partir deste campo
   *  quando o estado ainda não tem nenhum. Ausente: contexto antigo ou busca
   *  sem política — comportamento anterior. */
  policyLimits: HotelPolicyLimitsSchema.optional(),
  /** Contagem pré-corte da última busca (o `total` do painel/preview). */
  total: z.number().int().nonnegative().optional(),
});
export type HotelPlannerRefineContext = z.infer<typeof HotelPlannerRefineContextSchema>;

/**
 * Membro `hotel_planner` de `ChatUiSchema` — superfície pré-busca (espelho de
 * flight_planner). SEMPRE exibido durante a coleta de critérios. As datas são
 * strings TOLERANTES (semente do turno anterior; um valor passado/malformado
 * apenas não é pré-selecionado). Critérios válidos disparam automaticamente uma
 * `HotelPlannerSelection` em `challengeId` (guarded → submit_planner_search).
 *
 * Também serve como snapshot de resultado no mesmo lugar após a busca:
 * `offers` ausente significa ainda não buscado; `offers: []`, resultado vazio
 * concluído (estados distintos e válidos). `viewOnly` congela uma seleção
 * resolvida (`selectedOfferId`) — a busca e a troca de hotel, não o card inteiro:
 * a escolha do QUARTO vive no drawer de detalhes e acontece exatamente nesse
 * estado. `searchError` marca falha do provider sem descartar os critérios já
 * coletados.
 */
export const HotelPlannerBlockSchema = z.object({
  ...chatUiBase,
  kind: z.literal('hotel_planner'),
  challengeId: z.string().min(1),
  // Critérios completos disparam a busca sozinhos (comportamento histórico,
  // ausente = true). `false` é o card SEMEADO (#2152: critérios herdados da
  // viagem aberta, não ditos pelo cliente): a busca espera o gesto explícito —
  // semear e buscar sem confirmação seria decidir pelo viajante.
  autoSearch: z.boolean().optional(),
  place: z.string().min(1).optional(),
  placeDetails: HotelPlannerPlaceSchema.optional(),
  /** Evolução ADITIVA: ver `HotelPlaceSourceSchema`. Viaja no bloco para o
   *  destino re-semeado (voltar à busca) manter o veto de recente. */
  placeSource: HotelPlaceSourceSchema.optional(),
  destinationId: z.number().int().positive().optional(),
  checkin: z.string().min(1).optional(),
  checkout: z.string().min(1).optional(),
  rooms: HotelRoomsSchema.optional(),
  maxPrice: z.number().nonnegative().optional(),
  onlyBreakfast: z.boolean().optional(),
  onlyRefundable: z.boolean().optional(),
  onlyPolicyWithIn: z.boolean().optional(),
  travelers: HotelTravelersSchema.optional(),
  policyTravelerId: z.string().min(1).optional(),
  requesterId: z.string().min(1).optional(),
  /** Evolução ADITIVA: a sessão modal-session que produziu estas ofertas. O
   *  refino do card re-busca NELA em vez de mintar outra, para o id que o
   *  viajante escolhe ser o mesmo que o agente sabe resolver. Bloco antigo
   *  sem o campo continua parseando — o host volta a mintar sessão. */
  sessionId: z.string().min(1).optional(),
  /** Evolução ADITIVA: até quando as ofertas deste bloco valem — o `expiresAt`
   *  da sessão do fornecedor, que vence em 24 h e nunca é estendida. Passado o
   *  prazo, a BUSCA congela como com `viewOnly`: os preços do bloco já não
   *  valem, e refinar a partir deles prometeria uma lista que a sessão não sabe
   *  mais responder. Bloco antigo sem o campo nunca congela por tempo.
   *
   *  String TOLERANTE, e não `z.iso.datetime()`: um valor malformado não pode
   *  derrubar o parse do bloco inteiro — ele só não congela (ver
   *  `searchExpired` em `hotel_results_organism.tsx`). */
  expiresAt: z.string().min(1).optional(),
  offers: z.array(HotelOfferSchema).max(CHAT_UI_MAX_OFFERS).optional(),
  nights: z.number().int().min(1).optional(),
  selectedOfferId: z.string().min(1).optional(),
  selectedRoomIndex: z.number().int().positive().optional(),
  viewOnly: z.boolean().optional(),
  searchError: z.enum(['provider_error', 'timeout']).optional(),
  directives: HotelPlannerDirectivesSchema.optional(),
  /** Evolução ADITIVA (#3399 b): quantos hotéis a BUSCA trouxe — `offers` é o
   *  recorte que o card carrega (até `CHAT_UI_MAX_OFFERS`), e sem este número
   *  o card rotula "Menor preço" sobre 6 de 101 como se fosse a busca. Maior
   *  que `offers.length` ⇒ os selos de eixo dizem "no cartão". Bloco antigo
   *  sem o campo rotula como antes. */
  offersTotal: z.number().int().nonnegative().optional(),
});
export type HotelPlannerBlock = z.infer<typeof HotelPlannerBlockSchema>;

/**
 * Teto de UMA página do painel "ver todos". Deliberadamente maior que
 * `CHAT_UI_MAX_OFFERS`: aquele é o orçamento do frame SSE do bloco persistido
 * (que continua em 6 e é replayado no histórico), este é uma leitura sob
 * demanda, que só existe enquanto o painel está aberto.
 */
export const HOTEL_RESULTS_PAGE_MAX = 20;

/**
 * `Error.name` com que a porta `searchHotelPage` diz "a busca desta sessão
 * venceu" (o BFF responde 409 `search_stale`).
 *
 * O cache da busca do fornecedor tem vida curta — medido no develop: a
 * `searchKey` para de responder em ~10 minutos, e sem erro: página vazia com
 * contagem zero. Isso não é "nenhum hotel" nem falha de leitura, e a diferença
 * importa: o painel volta para as ofertas que o bloco trouxe (com a faixa de
 * busca ali para refazer) em vez de mostrar lista vazia ou pedir para repetir
 * uma leitura que nunca vai voltar.
 */
export const HOTEL_SEARCH_STALE = 'HOTEL_SEARCH_STALE';

/**
 * Os cortes que o PAINEL manda para o fornecedor aplicar sobre o conjunto
 * inteiro. Espelham 1:1 os eixos que a hotels-api sabe filtrar na leitura
 * paginada; o que ela não sabe (comodidade) e o que ela aceita e ignora (café
 * da manhã, reembolsável) fica no cliente, sobre o que já carregou.
 */
/** Faixa de nota: pelo menos um limite, e nunca invertida. Uma faixa vazia vira
 *  critério que não corta nada e uma invertida vira critério que não casa com
 *  nada — as duas chegariam ao fornecedor como filtro silenciosamente inútil. */
const HotelRatingRangeSchema = z
  .object({
    from: z.number().nonnegative().optional(),
    to: z.number().nonnegative().optional(),
  })
  .strict()
  .refine((range) => range.from !== undefined || range.to !== undefined, {
    message: 'rating range requires at least one bound',
  })
  .refine((range) => range.from === undefined || range.to === undefined || range.from <= range.to, {
    message: 'rating range is inverted',
  });

export const HotelResultsCutsSchema = z
  .object({
    minPrice: z.number().nonnegative().optional(),
    /** Diária, não total da estadia: o fornecedor guarda `totalPerNight` do
     *  quarto mais barato como a régua de preço do hotel, que é a mesma régua
     *  do slider do painel. */
    maxPrice: z.number().nonnegative().optional(),
    rating: z.array(HotelRatingRangeSchema).optional(),
    category: z.array(z.number().int().positive()).optional(),
    onlyRecommended: z.boolean().optional(),
    onlyPolicyWithIn: z.boolean().optional(),
    onlyAgreedRate: z.boolean().optional(),
    restrictRadiusTo: z.number().positive().optional(),
  })
  .strict()
  .refine(
    (cuts) =>
      cuts.minPrice === undefined || cuts.maxPrice === undefined || cuts.minPrice <= cuts.maxPrice,
    { message: 'price range is inverted', path: ['maxPrice'] },
  );
export type HotelResultsCuts = z.infer<typeof HotelResultsCutsSchema>;

/** Pedido de uma página do painel. `sessionId` não tem default: paginar exige a
 *  busca que já existe, e uma sessão nova devolveria outro conjunto. */
export const HotelResultsPageRequestSchema = z
  .object({
    sessionId: z.string().min(1),
    page: z
      .object({
        number: z.number().int().positive(),
        limit: z.number().int().positive().max(HOTEL_RESULTS_PAGE_MAX),
      })
      .strict(),
    cuts: HotelResultsCutsSchema.optional(),
    order: z.string().min(1).optional(),
  })
  .strict();
export type HotelResultsPageRequest = z.infer<typeof HotelResultsPageRequestSchema>;

/**
 * Página do painel de resultados: as ofertas mais onde elas caem no conjunto.
 * `hasNext` é o que o scroll infinito consulta para decidir se pede a próxima;
 * `total` é a contagem do conjunto FILTRADO inteiro e pode faltar (é uma leitura
 * à parte no fornecedor — sem ela o painel mostra o carregado, nunca um total
 * inventado).
 */
export const HotelResultsPageResultSchema = z.object({
  offers: z.array(HotelOfferSchema).max(HOTEL_RESULTS_PAGE_MAX),
  page: z
    .object({
      number: z.number().int().positive(),
      hasNext: z.boolean(),
      total: z.number().int().nonnegative().optional(),
    })
    .strict(),
});
export type HotelResultsPageResult = z.infer<typeof HotelResultsPageResultSchema>;
